/**
 * Global styles + the Prism code-highlighting theme are imported here so they
 * apply across every page. tailwind.css (Hezo design tokens + utilities) loads
 * first; global.css carries the marketing-site tokens and base styles.
 * @see https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/
 */
import "./src/styles/tailwind.css";
import "./src/styles/global.css";
import "./src/styles/prism.css";

import { installAnalytics } from "./src/lib/analytics";

export { wrapRootElement } from "./src/lib/wrap-root";
export { wrapPageElement } from "./src/lib/wrap-page";

/**
 * Navigating to a new page always lands at the top of the content. Anchor
 * links (#hash) keep their jump to the target, and back/forward puts the
 * reader back where they left off so history feels normal.
 *
 * The scroll must be `behavior: "instant"`: `html` carries
 * `scroll-behavior: smooth` for anchor jumps (src/styles/global.css), which
 * would otherwise turn this reset into an animation that starts just as the
 * new page's DOM swaps in. Navigating off a long page onto a shorter one then
 * shrinks the document mid-flight, the browser clamps the offset to the new
 * bottom, and the cancelled animation leaves the reader stranded mid-page.
 */
export const shouldUpdateScroll = ({
  routerProps: { location },
  getSavedScrollPosition,
}) => {
  // Anchor jump: the new page has rendered by now, so the target either exists
  // (let Gatsby scroll to it) or the hash is dead and we reset like any other
  // navigation rather than leaving the old offset behind.
  const id = location.hash && decodeURI(location.hash.slice(1));
  if (id && document.getElementById(id)) return true;

  // A position saved under this history entry's key means the reader has been
  // here before (back/forward), so restore it; anything else is a fresh
  // navigation and starts at the top. Gatsby hands back `[x, y]` with a null y
  // for a location it has never recorded.
  const saved = getSavedScrollPosition(location);
  const y = Array.isArray(saved) ? saved[1] : saved;
  window.scrollTo({ top: y || 0, left: 0, behavior: "instant" });
  return false;
};

/**
 * Publish the analytics hook, then first-visit language detection.
 *
 * Detection runs once, and only when the reader has never made an explicit
 * choice: if the browser asks for a language the site ships and we are on an
 * English URL, go to the localized twin. An explicit pick (the switcher writes
 * it) always wins afterwards, so someone who deliberately switches back to
 * English is never bounced again.
 *
 * Deliberately after hydration rather than in a blocking head script: the pages
 * are static per locale, so a redirect here changes the URL honestly instead of
 * rewriting content underneath a canonical tag that says otherwise.
 *
 * One consequence worth knowing when reading the numbers: that redirect races
 * the tracker's first pageview, which waits for the load event. When the load
 * event wins, Umami records a short pageview for the English URL the reader was
 * genuinely served for that moment, tagged `en`.
 */
export const onClientEntry = () => {
  // First, and outside the try below: detection returns early when storage is
  // blocked, and its catch must not swallow this.
  installAnalytics();

  try {
    const {
      DEFAULT_LANGUAGE,
      STORAGE_KEY,
      languageFromPath,
      localizedPath,
      negotiateLanguage,
    } = require("./src/lib/i18n");

    if (localStorage.getItem(STORAGE_KEY)) return;

    const path = window.location.pathname;
    // Only redirect off an English URL; never override an explicit /de/... link.
    if (languageFromPath(path) !== DEFAULT_LANGUAGE) return;

    const wanted = negotiateLanguage(navigator.languages || [navigator.language]);
    if (wanted === DEFAULT_LANGUAGE) return;

    const target = localizedPath(path, wanted);
    if (target !== path) window.location.replace(target + window.location.hash);
  } catch {
    // Storage blocked or an unexpected path shape - stay on the English page.
  }
};
